// El filtro por categoría de Mi marca → Productos, al lado del buscador
// (ADR-2026-10-22-filtro-por-categoria-en-productos). Reemplaza al botón que sólo abría el panel:
// el mismo menú del árbol, para elegir (sin crear), con "Todas" y "Sin categoría" arriba y "Editar
// categorías" abajo.
import React, { useRef, useState } from "react";
import { ListFilter, X } from "lucide-react";
import { MenuDesplegable } from "./MenuDesplegable";
import { partesDelCamino } from "../pages/lanzamientos/arbolDeCategorias";
import type { CategoriaDeTienda } from "../pages/lanzamientos/lanzamientosApi";
import { SIN_CATEGORIA } from "../lib/filtroPorCategoria";
import { MenuDeCategoria } from "./CategoriasDelProducto";

const TODAS = "__todas__";

/** Más ancho en escritorio, donde sobra lugar y los caminos largos no se cortan. */
const anchoDelMenu = (): number =>
  typeof window !== "undefined" &&
  window.matchMedia("(min-width: 640px)").matches
    ? 380
    : 300;

export const FiltroDeCategoria: React.FC<{
  categorias: CategoriaDeTienda[];
  cargando: boolean;
  /** null = todas. */
  filtro: string | null;
  onCambiar: (filtro: string | null) => void;
  /** Sin esto el menú no ofrece "Editar categorías" (Tienda online: se editan en la tienda). */
  onEditar?: () => void;
}> = ({ categorias, cargando, filtro, onCambiar, onEditar }) => {
  const ancla = useRef<HTMLButtonElement>(null);
  const [abierto, setAbierto] = useState(false);
  const activo = filtro !== null;
  // En el botón va sólo la última parte del camino ("Oversize"); el camino entero, en el título.
  const camino =
    filtro && filtro !== SIN_CATEGORIA
      ? partesDelCamino(categorias, filtro)
      : [];
  const etiqueta =
    filtro === SIN_CATEGORIA
      ? "Sin categoría"
      : (camino[camino.length - 1] ?? "Categoría");

  return (
    <div className="relative shrink-0">
      <button
        ref={ancla}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={abierto}
        onClick={() => setAbierto((a) => !a)}
        title={camino.length > 0 ? camino.join(" › ") : "Filtrar por categoría"}
        className={`inline-flex h-[var(--btn-h)] max-w-[200px] items-center gap-2 rounded-full border px-4 text-[13.5px] font-medium shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-colors ${
          activo
            ? "border-transparent bg-[rgb(var(--brand-primary-rgb)_/_0.085)] pr-10 text-brand-primary"
            : "border-black/[0.08] bg-white text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900"
        }`}
      >
        <ListFilter className="h-4 w-4 shrink-0" />
        <span
          className={`min-w-0 truncate ${activo ? "" : "hidden sm:inline"}`}
        >
          {etiqueta}
        </span>
      </button>
      {activo && (
        // Centrada con `inset-y-0 my-auto` y NO con `-translate-y-1/2`: el "apretón" global
        // (`button:active { transform: scale(.95) }`, index.css) pisa el transform, la × salta al
        // presionarla y el clic se pierde (ADR-2026-10-22-la-x-del-filtro-no-se-podia-tocar).
        <button
          type="button"
          onClick={() => onCambiar(null)}
          title="Sacar el filtro"
          aria-label="Sacar el filtro de categoría"
          className="absolute inset-y-0 right-1.5 my-auto inline-flex h-7 w-7 items-center justify-center rounded-full text-brand-primary transition-colors hover:bg-white/70"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      <MenuDesplegable
        abierto={abierto}
        anclaRef={ancla}
        onCerrar={() => setAbierto(false)}
        titulo="Filtrar por categoría"
        ancho={anchoDelMenu()}
        alineacion="derecha"
      >
        {(enTelefono) => (
          <MenuDeCategoria
            enfocarAlAbrir={!enTelefono}
            categorias={categorias}
            cargando={cargando}
            elegida={filtro ?? TODAS}
            extras={[
              { id: TODAS, etiqueta: "Todas las categorías" },
              { id: SIN_CATEGORIA, etiqueta: "Sin categoría" },
            ]}
            onElegir={(id) => {
              onCambiar(id === TODAS ? null : id);
              setAbierto(false);
            }}
            etiquetaEditar="Editar categorías"
            onEditar={
              onEditar &&
              (() => {
                setAbierto(false);
                onEditar();
              })
            }
          />
        )}
      </MenuDesplegable>
    </div>
  );
};
